import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Spinner } from "../components/ui";

const FIELDS = [
  { sec: "Identity", items: [["firstName", "First Name", "text", true], ["lastName", "Last Name", "text", true], ["email", "Email", "email", true], ["employeeCode", "Employee Code", "text"], ["phone", "Phone", "text"], ["gender", "Gender", "text"], ["dob", "Date of Birth", "date"]] },
  { sec: "Employment", items: [["jobTitle", "Job Title", "text"], ["joinDate", "Join Date", "date"]] },
];

export default function EmployeeForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { meta } = useAuth();
  const editing = Boolean(id);
  const [form, setForm] = useState({ status: "ACTIVE", employmentType: "FULL_TIME" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editing) api.get(`/employees/${id}`).then(({ data }) => {
      if (data.dob) data.dob = data.dob.slice(0, 10);
      if (data.joinDate) data.joinDate = data.joinDate.slice(0, 10);
      setForm(data);
    });
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const payload = { ...form };
      ["departmentId", "designationId", "locationId", "gradeId", "bandId"].forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editing) await api.put(`/employees/${id}`, payload);
      else await api.post("/employees", payload);
      nav("/employees");
    } catch (err) { setError(err.response?.data?.detail || "Failed to save"); setSaving(false); }
  };

  const selects = [["departmentId", "Department", meta?.departments], ["designationId", "Designation", meta?.designations, "title"], ["locationId", "Location", meta?.locations], ["gradeId", "Grade", meta?.grades], ["bandId", "Band", meta?.bands]];

  return (
    <div>
      <Link to="/employees" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-accent"><ArrowLeft size={15} /> Back</Link>
      <PageHeader title={editing ? "Edit Employee" : "New Employee"} subtitle={editing ? form.fullName : "Add a new member to SMIFS"} />
      <form onSubmit={submit} className="space-y-6" data-testid="employee-form">
        {FIELDS.map((g) => (
          <Card key={g.sec} className="p-6">
            <h3 className="mb-4 font-head text-base font-bold text-slate-900 dark:text-white">{g.sec}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {g.items.map(([k, label, type, req]) => (
                <div key={k}>
                  <label className="label">{label}{req && " *"}</label>
                  <input data-testid={`field-${k}`} type={type} required={req} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="input" />
                </div>
              ))}
            </div>
          </Card>
        ))}
        <Card className="p-6">
          <h3 className="mb-4 font-head text-base font-bold text-slate-900 dark:text-white">Organisation</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {selects.map(([k, label, opts, field]) => (
              <div key={k}>
                <label className="label">{label}</label>
                <select data-testid={`field-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="input">
                  <option value="">— Select —</option>
                  {opts?.map((o) => <option key={o.id} value={o.id}>{o[field || "name"]}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label className="label">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="input">
                {["ACTIVE", "INACTIVE", "TERMINATED"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </Card>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10">{String(error)}</p>}
        <div className="flex justify-end gap-3">
          <Link to="/employees" className="btn-outline">Cancel</Link>
          <button type="submit" disabled={saving} data-testid="save-employee" className="btn-accent">{saving ? <Spinner className="text-white" /> : "Save Employee"}</button>
        </div>
      </form>
    </div>
  );
}
